import React from 'react'
import { Box, Typography, TextField, IconButton, Button } from '@mui/material'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useDispatch } from 'react-redux'
import {
  createDesignation,
  updateDesignation,
} from '../../slices/designationsApiSlice'

const DesignationForm = ({
  designation,
  onSave,
  isAdding,
  existingCodes = [],
  onCancel,
  showSnackbar,
  setIsAdding,
}) => {
  const dispatch = useDispatch()

  const initialValues = isAdding
    ? {
        code: '',
        name: '',
        percentage: '',
      }
    : {
        id: designation?.id || '',
        code: designation?.code || '',
        name: designation?.name || '',
        percentage: designation?.percentage || '',
      }

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      code: Yup.string()
        .required('Code is Mandatory')
        .min(2, 'minimum 2 characters required')
        .max(3, 'maximum 3 characters allowed')
        .matches(/^[a-zA-Z0-9]+$/, 'Only alphabetic allowed')
        .test('unique-code', 'Code already exists', (value) => {
          return isAdding ? !existingCodes.includes(value?.toUpperCase()) : true
        }),

      name: Yup.string()
        .required('Name is Mandatory')
        .matches(/^[A-Za-z0-9\s]+$/, 'Only alphabetic allowed'),
      percentage: Yup.number()
        .required('Commission Percentage is Mandatory')
        .min(0, 'Percentage must be at least 0')
        .max(100, 'Percentage must be at most 100'),
    }),
    onSubmit: (values) => {
      if (isAdding) {
        dispatch(createDesignation(values))
          .then(() => {
            showSnackbar('Designation created successfully', 'success')
            setIsAdding(true)
            formik.resetForm()
          })
          .catch(() => showSnackbar('Failed to create designation', 'error'))
      } else {
        dispatch(updateDesignation(values))
          .then(() => {
            showSnackbar('Designation updated successfully', 'success')
            setIsAdding(true)
            formik.resetForm()
          })
          .catch(() => showSnackbar('Failed to update designation', 'error'))
      }
      formik.resetForm()
    },
  })

  return (
    <Box
      sx={{
        backgroundColor: '#F6F9FE',
        padding: 2,
        borderRadius: '20px',
        boxShadow: 1,
        height: '100%',
        margin: 'auto',
      }}>
      {/* Title */}
      <Box sx={{ mb: 1, mt: 1 }}>
        <Typography variant='h5' sx={{ fontWeight: 'bold' }}>
          {isAdding ? (
            <>
              <span style={{ color: '#F5774D' }}>Add</span> Designation
            </>
          ) : (
            <>
              <span style={{ color: '#F5774D' }}>Edit</span> Designation
            </>
          )}
        </Typography>
      </Box>

      {/* Input Fields parent box */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          formik.handleSubmit(e)
        }}>
        <Box sx={{ mb: 3 }}>
          {/* Code field */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label='Code'
              variant='filled'
              name='code'
              value={formik.values.code}
              onChange={(e) => {
                const value = e.target.value.toUpperCase()
                if (value.length <= 3) {
                  formik.setFieldValue('code', value)
                }
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.code && Boolean(formik.errors.code)}
              helperText={formik.touched.code && formik.errors.code}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => formik.setFieldValue('code', '')}>
                    <HighlightOffIcon sx={{ color: 'grey' }} />
                  </IconButton>
                ),
                sx: { backgroundColor: isAdding ? '#FFFFFF' : '#E1E4E9' },
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                mb: 1,
                marginBottom: 3,
                borderRadius: 1,
              }}
              disabled={!isAdding}
            />
          </Box>

          {/* Name field */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label='Name'
              name='name'
              value={formik.values.name}
              onChange={formik.handleChange}
              variant='filled'
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => formik.setFieldValue('name', '')}>
                    <HighlightOffIcon sx={{ color: 'grey' }} />
                  </IconButton>
                ),
                sx: { backgroundColor: '#FFFFFF', borderRadius: 1 },
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1, marginBottom: 3 }}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Box>

          {/* Percentage field */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label='Commission Percentage (%)'
              name='percentage'
              value={formik.values.percentage}
              inputMode='numeric'
              onChange={(e) => {
                const value = e.target.value
                if (
                  value === '' ||
                  (Number(value) >= 0 && Number(value) <= 100)
                ) {
                  formik.handleChange(e)
                }
              }}
              variant='filled'
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => formik.setFieldValue('percentage', '')}>
                    <HighlightOffIcon sx={{ color: 'grey' }} />
                  </IconButton>
                ),
                sx: { backgroundColor: '#FFFFFF', borderRadius: 1 },
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1, marginBottom: 3 }}
              onBlur={formik.handleBlur}
              error={
                formik.touched.percentage && Boolean(formik.errors.percentage)
              }
              helperText={formik.touched.percentage && formik.errors.percentage}
            />
          </Box>
        </Box>

        {/* Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            marginRight: '32px',
          }}>
          <Button
            variant='contained'
            type='submit'
            sx={{
              backgroundColor: '#F5774D',
              color: '#FFFFFF',
              borderRadius: '20px',
              px: 4,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#FF5C33',
              },
            }}>
            {isAdding ? 'Add' : 'Save'}
          </Button>
          <Button
            variant='contained'
            onClick={() => {
              formik.resetForm()
              if (onCancel) onCancel()
            }}
            sx={{
              backgroundColor: '#C5C5C5',
              color: '#FFFFFF',
              borderRadius: '20px',
              px: 3,
              textTransform: 'none',
              ml: 2,
              '&:hover': {
                backgroundColor: '#A5A5A5',
              },
            }}>
            Cancel
          </Button>
        </Box>
      </form>
    </Box>
  )
}

export default DesignationForm
